import { monthGrid, startOfMonth, type Day } from './dates';

/**
 * Months a guest cannot use, folded away.
 *
 * ---------------------------------------------------------------------------
 * WHY FOLD AND NOT DELETE
 * ---------------------------------------------------------------------------
 *
 * De Vergarde is shut from October to late March. Between choosing an arrival
 * and finding the next open date a guest scrolled six grids of uniformly grey
 * squares, and a run of dead months reads as a broken calendar rather than as a
 * closed park — the operator hit exactly that: "dates from 1 october to 24
 * march are selectable, its then closed nobody can book".
 *
 * Removing them silently trades one confusion for another. Somebody looking for
 * November finds the month simply absent, with nothing to say whether the park
 * is closed, the site is broken, or they have scrolled past it. So a run
 * collapses to ONE line that names the stretch and says why. Shorter than six
 * grids, and it answers the question the grids were failing to answer.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS NEVER FOLDED
 * ---------------------------------------------------------------------------
 *
 * Only what is KNOWN to be unusable. A month is folded when the data covers it
 * and nothing in it can be picked — never because an answer has not arrived,
 * never because a filter matched nothing (that is a sentence above the grid,
 * not a fact about the park), and never a month holding a date the guest has
 * already chosen or the first date they may still choose.
 *
 * The rule is derived, not configured. Nothing here knows what a season is:
 * a month folds because every day in it failed the same predicate that greys a
 * single square, which is built from the periods and rules the API sent.
 */

export type CalendarRow =
  | { kind: 'month'; month: Day }
  /** A run of months nothing can be picked in. `count` is how many. */
  | { kind: 'gap'; from: Day; to: Day; count: number };

export interface RowOptions {
  /** Whether a given day can be picked. The same predicate that greys a cell. */
  selectable: (day: Day) => boolean;
  /**
   * First day nothing is known about. A month at or past it is drawn, because
   * "we have not asked yet" and "there is nothing there" are different answers
   * and only one of them may be folded away.
   */
  unknownBeyond?: Day | null;
  /** Dates that must stay on screen whatever the predicate says. */
  keep?: ReadonlyArray<Day | null>;
  /** Fold nothing at all. Set while an answer is missing, or a filter is empty. */
  disabled?: boolean;
}

/** Whether any day of `month` can be picked. */
function monthHasAny(month: Day, selectable: (day: Day) => boolean): boolean {
  for (const day of monthGrid(month)) {
    // `monthGrid` returns a flat 42-cell grid padded with the neighbouring
    // months' days so the weeks line up. Those belong to another row and must
    // not keep this one alive.
    if (startOfMonth(day) === month && selectable(day)) return true;
  }
  return false;
}

export function calendarRows(months: readonly Day[], options: RowOptions): CalendarRow[] {
  const { selectable, unknownBeyond = null, keep = [], disabled = false } = options;
  const kept = keep.filter((d): d is Day => d !== null).map(startOfMonth);

  const foldable = months.map((month) => {
    if (disabled) return false;
    if (kept.includes(month)) return false;
    // Unknown, not empty.
    if (unknownBeyond !== null && month >= startOfMonth(unknownBeyond)) return false;
    return !monthHasAny(month, selectable);
  });

  const rows: CalendarRow[] = [];
  for (let i = 0; i < months.length; i += 1) {
    if (!foldable[i]) { rows.push({ kind: 'month', month: months[i]! }); continue; }
    let j = i;
    while (j + 1 < months.length && foldable[j + 1]) j += 1;
    rows.push({ kind: 'gap', from: months[i]!, to: months[j]!, count: j - i + 1 });
    i = j;
  }
  return rows;
}
